import { useRef } from 'react';

import { useAppState } from '../../../AppStateProvider';
import { ErrorBoundaryComponent } from '../../../components/ErrorBoundary/ErrorBoundary';

import { AIOutreachAlternativeInsights } from './AIOutreachAlternativeInsights';
import { AIOutreachChatBar } from './AIOutreachChatBar';
import { AIOutreachChatErrorBoundaryFallback } from './AIOutreachChatRenderErrorFallback';
import { AIOutreachCompetitorPicker } from './AIOutreachCompetitorPicker';
import { AIOutreachContent } from './AIOutreachContent';
import { AIOutreachDisclaimer } from './AIOutreachDisclaimer';
import { AIOutreachForm } from './AIOutreachForm';
import { AIOutreachHeader } from './AIOutreachHeader';
import { AIOutreachInitialLoader } from './AIOutreachInitialLoader';
import {
  StyledBody,
  StyledBodyErrorBoundaryWrap,
  StyledBodyInitialLoading,
  StyledContentArea,
  StyledDrawer,
  StyledOverlay,
} from './styles';
import { useAiOutreach } from './useAiOutreach';

const AIOutreachPanelContent = ({ isOpen }: { isOpen: boolean }) => {
  const {
    domain,
    companyName,
    isInitialLoading,
    isFollowUpLoading,
    timeline,
    scrollToTopAfterUserSend,
    isError,
    onRetry,
    selectedRecipient,
    threadId,
    emailPreviews,
    competitors,
    showAlternatives,
    showCompetitorPicker,
    onGenerate,
    onClose,
    onSelectRecipient,
    onSendMessage,
    onApplyPreview,
    onDismissAlternatives,
    onApplyCompetitors,
    onDismissCompetitorPicker,
  } = useAiOutreach();

  const hasThreadContent = timeline.length > 0 || isFollowUpLoading;
  const showChatBar =
    Boolean(threadId) && !isInitialLoading && hasThreadContent && !showAlternatives && !showCompetitorPicker;

  return (
    <StyledOverlay isOpen={isOpen} data-automation="AIOutreachOverlay">
      <StyledDrawer isOpen={isOpen}>
        <AIOutreachHeader onClose={onClose} />
        <StyledContentArea>
          <StyledBody>
            <AIOutreachForm
              domain={domain}
              companyName={companyName}
              isPending={isInitialLoading}
              selectedRecipient={selectedRecipient}
              onSelectRecipient={onSelectRecipient}
            />
            <StyledBodyErrorBoundaryWrap>
              <ErrorBoundaryComponent FallbackComponent={AIOutreachChatErrorBoundaryFallback}>
                {isInitialLoading ? (
                  <StyledBodyInitialLoading>
                    <AIOutreachInitialLoader />
                  </StyledBodyInitialLoading>
                ) : showAlternatives ? (
                  <AIOutreachAlternativeInsights
                    emailPreviews={emailPreviews}
                    onApplyPreview={onApplyPreview}
                    onBack={onDismissAlternatives}
                  />
                ) : (
                  <AIOutreachContent
                    isFollowUpLoading={isFollowUpLoading}
                    timeline={timeline}
                    isError={isError}
                    onRetry={onRetry}
                    threadId={threadId}
                    scrollToTopAfterUserSend={scrollToTopAfterUserSend}
                    onGenerate={onGenerate}
                  />
                )}
              </ErrorBoundaryComponent>
            </StyledBodyErrorBoundaryWrap>
          </StyledBody>
          {showChatBar ? (
            <>
              <AIOutreachChatBar
                onSendMessage={onSendMessage}
                isDisabled={isFollowUpLoading || isError}
                hidePredefinedChips={isFollowUpLoading}
              />
              <AIOutreachDisclaimer />
            </>
          ) : null}
          {showCompetitorPicker ? (
            <AIOutreachCompetitorPicker
              competitors={competitors}
              prospectDomain={domain ?? ''}
              onApply={onApplyCompetitors}
              onBack={onDismissCompetitorPicker}
            />
          ) : null}
        </StyledContentArea>
      </StyledDrawer>
    </StyledOverlay>
  );
};

export const AIOutreachPanel = () => {
  const {
    aiOutreach: { isOpen: isAiOutreachOpen },
  } = useAppState();

  const hasMountedRef = useRef(false);
  if (isAiOutreachOpen) {
    hasMountedRef.current = true;
  }

  if (!hasMountedRef.current) {
    return null;
  }

  return <AIOutreachPanelContent isOpen={isAiOutreachOpen} />;
};
